import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Target, Eye, Users, Handshake, Shield, CheckCircle, Lightbulb } from 'lucide-react';

const VisionSourcing = () => {
  const { language } = useLanguage();

  const sections = {
    intro: {
      title: language === 'fr' ? "Une Vision du Sourcing Moderne" : "A Vision for Modern Sourcing",
      subtitle: language === 'fr' 
        ? "Le sourcing n'est pas un exercice d'achat. C'est une transformation de la confiance opérationnelle."
        : "Sourcing is not a procurement exercise. It is a transformation of operating trust.",
      description: language === 'fr'
        ? "Pour de nombreuses organisations, le sourcing est encore traité comme une négociation commerciale : définir le périmètre, lancer un RFP, comparer les propositions et sélectionner un fournisseur. Mais la réalité de l'outsourcing à grande échelle révèle une vérité différente. La plupart des échecs de sourcing ne viennent pas du pricing ou des termes contractuels. Ils viennent de modèles opérationnels mal alignés, d'attentes irréalistes et de partenariats fragiles. Le sourcing moderne doit donc évoluer de la sélection de fournisseurs vers l'architecture de partenariat."
        : "For many organizations, sourcing is still treated as a commercial negotiation: defining scope, launching an RFP, comparing proposals and selecting a vendor. Yet the reality of large-scale outsourcing shows a different truth. Most sourcing failures do not come from pricing or contractual terms. They come from misaligned operating models, unrealistic expectations and fragile partnerships. Modern sourcing must therefore evolve from vendor selection to partnership architecture."
    },
    vendorSelection: {
      title: language === 'fr' ? "La Réalité de la Sélection Fournisseur" : "The Reality of Vendor Selection",
      description: language === 'fr'
        ? "Un fournisseur ne se choisit pas sur papier — il se révèle dans l'interaction. Les présentations PowerPoint, listes de références et propositions polies prédisent rarement la réalité opérationnelle."
        : "A vendor is not chosen on paper — it is revealed in interaction. Slideware, reference lists and polished proposals rarely predict operational reality.",
      points: language === 'fr' ? [
        "Comment ils challengent les hypothèses",
        "Comment ils gèrent l'ambiguïté", 
        "Comment ils collaborent avec les équipes internes",
        "Comment ils adaptent leur modèle au contexte du client"
      ] : [
        "How they challenge assumptions",
        "How they engage with ambiguity",
        "How they collaborate with internal teams",
        "How they adapt their model to the client's context"
      ],
      conclusion: language === 'fr'
        ? "La due diligence doit donc être rythmée, expérientielle et ancrée dans la réalité du client. Le vrai insight sourcing vient de l'observation des fournisseurs en mouvement, pas de la lecture de présentations. Les signaux humains et systémiques prédisent le succès à long terme bien plus fiablement que les slides."
        : "Due diligence must therefore be paced, experiential and grounded in the client's reality. Real sourcing insight comes from observing vendors in motion, not from reading proposals. Human and systemic signals predict long-term success far more reliably than presentation decks."
    },
    newRole: {
      title: language === 'fr' ? "Le Nouveau Rôle du Sourcing Stratégique" : "The New Role of Strategic Sourcing",
      description: language === 'fr'
        ? "Dans un monde de modèles de delivery hybrides, d'intégrateurs de services globaux, de services pilotés par l'IA et d'écosystèmes de plateformes, le sourcing doit désormais servir trois objectifs stratégiques :"
        : "In a world of hybrid delivery models, global service integrators, AI-driven services and platform ecosystems, sourcing must now serve three strategic objectives:",
      objectives: [
        {
          title: language === 'fr' ? "1. Stabiliser les fondamentaux" : "1. Stabilize the fundamentals",
          description: language === 'fr'
            ? "Avant que l'innovation puisse se produire, l'épine dorsale opérationnelle doit être fiable. Le bon partenaire aide l'organisation à stabiliser : la qualité de service, la responsabilité opérationnelle, la clarté de gouvernance, la transparence des coûts. La stabilité crée les conditions de la transformation."
            : "Before innovation can happen, the operational backbone must be reliable. The right partner helps the organization stabilize: service quality, operational accountability, governance clarity, cost transparency. Stability creates the conditions for transformation."
        },
        {
          title: language === 'fr' ? "2. Construire un modèle opérationnel adapté à la réalité" : "2. Build an operating model that fits reality",
          description: language === 'fr'
            ? "Un partenaire fort n'impose pas son modèle. Les meilleurs fournisseurs élèvent l'organisation du client en apportant structure et discipline de delivery, en introduisant des standards sectoriels, en clarifiant les rôles et responsabilités, en simplifiant la complexité. Mais ils le font dans la culture, la maturité et les contraintes du client. L'objectif n'est pas de répliquer un modèle fournisseur, mais de concevoir un Target Operating Model qui s'adapte vraiment."
            : "A strong partner does not impose its model. The best vendors elevate the client organization by bringing structure and delivery discipline, introducing industry standards, clarifying roles and accountability, simplifying complexity. But they do so within the client's culture, maturity and constraints. The objective is not to replicate a vendor model, but to design a Target Operating Model that truly fits."
        },
        {
          title: language === 'fr' ? "3. Créer une dynamique de partenariat long terme" : "3. Create long-term partnership dynamics",
          description: language === 'fr'
            ? "Les relations d'outsourcing échouent souvent non pas à cause de la technologie, mais à cause d'incitations mal alignées et de structures de gouvernance défaillantes. Le sourcing stratégique doit donc concevoir des partenariats qui équilibrent contrôle et autonomie, alignent les incitations sur les résultats, favorisent la transparence, créent une responsabilité partagée. L'objectif n'est pas la dépendance fournisseur, mais un écosystème de partenariat mature et gouverné."
            : "Outsourcing relationships often fail not because of technology, but because of misaligned incentives and governance structures. Strategic sourcing must therefore design partnerships that balance control and autonomy, align incentives on outcomes, foster transparency, create shared accountability. The goal is not vendor dependency, but a mature, governed partnership ecosystem."
        }
      ]
    },
    walabyPerspective: {
      title: language === 'fr' ? "La Perspective Walaby" : "The Walaby Perspective",
      intro: language === 'fr'
        ? "Chez Walaby, nous croyons que le sourcing devrait être un parcours d'apprentissage, pas un exercice transactionnel. Notre rôle n'est pas simplement de gérer un RFP. Nous aidons les organisations à comprendre, tester et façonner leurs futurs partenariats."
        : "At Walaby, we believe sourcing should be a learning journey, not a transactional exercise. Our role is not simply to manage an RFP. We help organizations understand, test and shape their future partnerships.",
      principles: [
        {
          icon: Target,
          title: language === 'fr' ? "Nous concevons des processus qui testent la réalité, pas le théâtre" : "We design sourcing processes that test reality, not theater",
          description: language === 'fr'
            ? "Plutôt que de nous fier uniquement aux propositions, nous créons des interactions qui révèlent : la posture de delivery, le style de collaboration, la maturité du leadership, le comportement de résolution de problèmes. Ces signaux sont bien plus prédictifs du succès à long terme que les engagements écrits."
            : "Rather than relying solely on proposals, we create interactions that reveal: delivery posture, collaboration style, leadership maturity, problem-solving behavior. These signals are far more predictive of long-term success than written commitments."
        },
        {
          icon: Eye,
          title: language === 'fr' ? "Nous observons les signaux qui comptent" : "We observe the signals that matter",
          description: language === 'fr'
            ? "Lors des interactions avec les fournisseurs, nous regardons au-delà des slides. Nous évaluons : comment les fournisseurs challengent les hypothèses, comment ils gèrent l'ambiguïté, comment ils collaborent avec les parties prenantes Business et IT, leur transparence sur les risques et contraintes. Ces signaux humains et systémiques déterminent la force du futur partenariat."
            : "During vendor interactions, we look beyond the slides. We assess: how vendors challenge assumptions, how they deal with ambiguity, how they collaborate with Business and IT stakeholders, how transparent they are on risks and constraints. These human and systemic signals determine the strength of the future partnership."
        },
        {
          icon: Users,
          title: language === 'fr' ? "Nous alignons Business, IT et Achats autour d'une vision sourcing" : "We align Business, IT and Procurement around one sourcing vision",
          description: language === 'fr'
            ? "Beaucoup d'initiatives sourcing échouent parce que les parties prenantes poursuivent des objectifs différents : le Business cherche l'agilité, l'IT cherche la stabilité, les Achats cherchent l'optimisation des coûts. Walaby apporte rigueur sourcing et coaching systémique pour aligner ces perspectives et créer une vision partagée."
            : "Many sourcing initiatives fail because stakeholders pursue different objectives: Business seeks agility, IT seeks stability, Procurement seeks cost optimization. Walaby brings sourcing rigor and systemic coaching to align these perspectives and create a shared vision."
        },
        {
          icon: Lightbulb,
          title: language === 'fr' ? "Nous structurons la due diligence comme un parcours d'apprentissage" : "We structure due diligence as a learning journey",
          description: language === 'fr'
            ? "L'évaluation fournisseur ne devrait pas seulement aider à sélectionner un partenaire. Elle devrait aider l'organisation à : clarifier son modèle opérationnel, affiner ses attentes, comprendre les capacités des fournisseurs, construire sa maturité de gouvernance. Un processus sourcing bien conçu renforce à la fois le client et l'écosystème fournisseur."
            : "Vendor evaluation should not only help select a partner. It should help the organization: clarify its operating model, refine its expectations, understand vendor capabilities, build governance maturity. A well-designed sourcing process strengthens both the client and the vendor ecosystem."
        },
        {
          icon: Shield,
          title: language === 'fr' ? "Nous assurons un processus équitable, transparent et humain" : "We ensure a fair, transparent and human process",
          description: language === 'fr'
            ? "La confiance commence pendant le processus sourcing lui-même. Une évaluation équitable et transparente : renforce la crédibilité avec les fournisseurs, réduit les frictions internes, accélère l'alignement des parties prenantes, crée une appropriation précoce entre les delivery towers. Un processus équitable attire aussi de meilleurs partenaires."
            : "Trust begins during the sourcing process itself. A fair and transparent evaluation: builds credibility with vendors, reduces internal friction, accelerates stakeholder alignment, creates early ownership across delivery towers. A fair process also attracts better partners."
        },
        {
          icon: Handshake,
          title: language === 'fr' ? "Nous préparons nos clients à gouverner leurs partenaires dès le premier jour" : "We prepare our clients to govern their partners from day one",
          description: language === 'fr'
            ? "Le vrai succès du sourcing commence après la signature du contrat. Walaby aide les organisations à établir : les frameworks de gouvernance, le performance management, les mécanismes d'escalade, les rituels de collaboration. Cela garantit que le partenariat démarre sur des fondations solides."
            : "The true success of sourcing begins after contract signature. Walaby helps organizations establish: governance frameworks, performance management, escalation mechanisms, collaboration rituals. This ensures the partnership starts on strong foundations."
        }
      ]
    },
    outcome: {
      title: language === 'fr' ? "Le Résultat" : "The Outcome",
      description: language === 'fr'
        ? "Quand le sourcing est bien fait, il fait plus que sélectionner des fournisseurs. Il :"
        : "When sourcing is done right, it does more than select vendors. It:",
      points: language === 'fr' ? [
        "Stabilise les opérations",
        "Clarifie les responsabilités",
        "Accélère la confiance",
        "Permet la transformation"
      ] : [
        "Stabilizes operations",
        "Clarifies accountability",
        "Accelerates trust",
        "Enables transformation"
      ],
      conclusion: language === 'fr'
        ? "Un processus sourcing équitable, transparent et expérientiel crée quelque chose de bien plus précieux qu'un contrat. Il crée un écosystème de partenariat capable de délivrer de la valeur à long terme."
        : "A fair, transparent and experiential sourcing process creates something far more valuable than a contract. It creates a partnership ecosystem capable of delivering long-term value."
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="vision-sourcing-page">
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="font-heading text-sm font-semibold tracking-[0.2em] text-[#E5A81D] mb-4 block animate-fade-in-up">
            {language === 'fr' ? 'NOTRE VISION' : 'OUR VISION'}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#0F172A] mb-6 animate-fade-in-up animation-delay-100">
            {sections.intro.title}
          </h1>
          <p className="font-heading text-xl md:text-2xl text-[#E5A81D] font-semibold mb-6 animate-fade-in-up animation-delay-200">
            {sections.intro.subtitle}
          </p>
          <p className="font-body text-lg text-slate-600 leading-relaxed max-w-4xl animate-fade-in-up animation-delay-300">
            {sections.intro.description}
          </p>
        </div>
      </section>

      {/* Vendor Selection Reality */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0F172A] mb-6">
                {sections.vendorSelection.title}
              </h2>
              <p className="font-body text-slate-600 leading-relaxed mb-8">
                {sections.vendorSelection.description}
              </p>
              <p className="font-body text-slate-700 font-medium mb-4">
                {language === 'fr' ? "Ce qui révèle vraiment la capacité d'un partenaire à délivrer :" : "What truly reveals a partner's ability to deliver:"}
              </p>
              <ul className="space-y-3 mb-8">
                {sections.vendorSelection.points.map((point, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-[#E5A81D] flex-shrink-0 mt-0.5" />
                    <span className="font-body text-slate-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#0F172A] p-8 text-white">
              <p className="font-body leading-relaxed text-slate-300">
                {sections.vendorSelection.conclusion}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* New Role of Strategic Sourcing */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0F172A] mb-6">
            {sections.newRole.title}
          </h2>
          <p className="font-body text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl">
            {sections.newRole.description}
          </p>
          
          <div className="space-y-8">
            {sections.newRole.objectives.map((objective, index) => (
              <div key={index} className="bg-white p-8 border border-slate-200 shadow-card">
                <h3 className="font-heading text-xl font-semibold text-[#E5A81D] mb-4">
                  {objective.title}
                </h3>
                <p className="font-body text-slate-600 leading-relaxed">
                  {objective.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Walaby Perspective */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0F172A] mb-6">
            {sections.walabyPerspective.title}
          </h2>
          <p className="font-body text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl">
            {sections.walabyPerspective.intro}
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {sections.walabyPerspective.principles.map((principle, index) => (
              <div key={index} className="bg-white p-8 border border-slate-200 hover:border-[#E5A81D]/50 transition-all duration-300 hover:shadow-hover">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#E5A81D]/10 flex-shrink-0">
                    <principle.icon size={24} className="text-[#E5A81D]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-3">
                      {principle.title}
                    </h3>
                    <p className="font-body text-sm text-slate-600 leading-relaxed">
                      {principle.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcome */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            {sections.outcome.title}
          </h2>
          <p className="font-body text-lg text-slate-300 mb-8">
            {sections.outcome.description}
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {sections.outcome.points.map((point, index) => (
              <span key={index} className="bg-[#E5A81D]/20 text-[#E5A81D] px-6 py-3 font-heading text-sm font-medium">
                {point}
              </span>
            ))}
          </div>
          <p className="font-body text-slate-300 max-w-3xl mx-auto mb-10">
            {sections.outcome.conclusion}
          </p>
          <Link
            to="/contact"
            data-testid="vision-cta"
            className="inline-flex items-center justify-center bg-[#E5A81D] text-[#0F172A] px-8 py-4 font-heading text-sm font-bold hover:bg-[#E5A81D]/90 hover:shadow-lg transition-all duration-300"
          >
            {language === 'fr' ? 'Discutons de votre stratégie sourcing' : 'Let\'s discuss your sourcing strategy'}
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default VisionSourcing;
